import React, { memo, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Box, FormField, Label, Select, ShadowOverlay, Tooltip, getPaletteColor } from 'pcln-design-system'
import { Warning } from 'pcln-icons'
import generateTimeOptions from '../utils/generateTimeOptions'

const StyledLabel = styled(Label)`
  z-index: ${(props) => props.zIndex};
`

const StyledSelect = styled(Select)`
  background-color: ${getPaletteColor('background.lightest')};
  z-index: ${(props) => props.zIndex};
`

const WarningIcon = styled(Warning)`
  z-index: ${(props) => props.zIndex};
`

function TimePicker({ hasShadowEffect, label, value, zIndex, onChange, errorMessage, ...props }) {
  const [isOpen, setOpen] = useState(false)

  const handleChange = (evt) => {
    setOpen(false)
    onChange && onChange(evt)
  }

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const handleKeyDown = (evt) => {
    const key = evt.key
    if (key === 'Escape') {
      setOpen(false)
    }
  }

  const timeOptions = useMemo(() => generateTimeOptions(), [])

  const showError = errorMessage !== undefined && errorMessage !== ''

  return (
    <>
      {isOpen && hasShadowEffect && (
        <ShadowOverlay zIndex={zIndex} onClick={handleClose} data-testid='time-picker-shadow-overlay' />
      )}
      <Box>
        <FormField>
          {label && <StyledLabel zIndex={zIndex + 1}>{label}</StyledLabel>}
          <StyledSelect
            aria-label={label}
            zIndex={zIndex}
            value={value}
            onBlur={handleClose}
            onChange={handleChange}
            onClick={handleOpen}
            onFocus={handleOpen}
            onKeyDown={handleKeyDown}
            color={errorMessage && 'error'}
            {...props}
          >
            {timeOptions &&
              timeOptions.map((time) => (
                <option key={time.value} {...time}>
                  {time.label}
                </option>
              ))}
          </StyledSelect>
          {showError && <WarningIcon color='error' size={20} zIndex={zIndex} />}
        </FormField>
        {!isOpen && showError && (
          <Tooltip bottom right color='error' zIndex={zIndex}>
            {errorMessage}
          </Tooltip>
        )}
      </Box>
    </>
  )
}

TimePicker.propTypes = {
  hasShadowEffect: PropTypes.bool,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  zIndex: PropTypes.number,
  onChange: PropTypes.func,
  errorMessage: PropTypes.string,
}

TimePicker.defaultProps = {
  value: '12:00',
  zIndex: 1,
}

export default memo(TimePicker)
